/**
 * Reusable specs-accordion
 * ------------------------------------------------------------------
 * Markup:
 *   <div class="specs-accordion" data-specs-accordion>      (optional wrapper — scopes "close others")
 *     <div class="specs-accordion-item">
 *       <button class="specs-accordion-header" data-target="PANEL_ID" aria-controls="PANEL_ID" aria-expanded="false">
 *         <span>Title</span>
 *         <span class="specs-toggle" aria-hidden="true"></span>
 *       </button>
 *       <div class="specs-accordion-panel" id="PANEL_ID">…</div>
 *     </div>
 *   </div>
 *
 * Behaviour: single panel open at a time within its [data-specs-accordion] scope
 * (or page-wide if no wrapper). Add `data-allow-multiple` on the wrapper to keep
 * several open at once. Uses event delegation, so accordions added later
 * (e.g. in the Shopify theme editor) work without re-initialising.
 */
(function () {
  function panelFor(header) {
    if (header.dataset.target) {
      var byId = document.getElementById(header.dataset.target);
      if (byId) return byId;
    }
    var item = header.closest('.specs-accordion-item') || header.parentElement;
    return item ? item.querySelector('.specs-accordion-panel') : null;
  }

  function setState(header, panel, open) {
    header.classList.toggle('specs-active', open);
    header.setAttribute('aria-expanded', open ? 'true' : 'false');

    var toggle = header.querySelector('.specs-toggle');
    if (toggle) toggle.classList.toggle('specs-active', open);

    if (panel) panel.classList.toggle('open', open);
  }

  function toggle(header) {
    var panel = panelFor(header);

    if (header.classList.contains('specs-active')) {
      setState(header, panel, false);
      return;
    }

    var scope = header.closest('[data-specs-accordion]') || document;
    var allowMultiple = scope !== document && scope.hasAttribute('data-allow-multiple');

    if (!allowMultiple) {
      scope.querySelectorAll('.specs-accordion-header.specs-active').forEach(function (other) {
        if (other !== header) setState(other, panelFor(other), false);
      });
    }

    setState(header, panel, true);
  }

  // Click (also fires for keyboard activation of a <button> header).
  document.addEventListener('click', function (e) {
    var header = e.target.closest('.specs-accordion-header');
    if (header) toggle(header);
  });

  // Keyboard support for non-button headers (e.g. a div with role="button").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var header = e.target.closest('.specs-accordion-header');
    if (!header || header.tagName === 'BUTTON' || header.tagName === 'A') return;
    e.preventDefault();
    toggle(header);
  });
})();





async function applySellingPlan({ lineKey, sellingPlanId, quantity = 1, btn = null }) {
  if (!lineKey || !sellingPlanId) return;

  const root = window.Shopify?.routes?.root || "/";

  if (btn) {
    btn.classList.add("loading_tr");
    btn.disabled = true;
  }

  try {
    // Get the current cart so we can convert line item key -> line number
    const cartRes = await fetch(`${root}cart.js`);
    const cart = await cartRes.json();

    const lineIndex = cart.items.findIndex(item => item.key === lineKey);

    if (lineIndex === -1) {
      throw new Error(`Line item not found for key: ${lineKey}`);
    }

    const response = await fetch(`${root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        line: lineIndex + 1,
        quantity: Number(quantity) || 1,
        selling_plan: Number(sellingPlanId)
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.description || data?.message || `Cart change failed: ${response.status}`);
    }

    const cartDrawerSlide = document.querySelector("#cart-drawer");
    if (cartDrawerSlide && cartDrawerSlide._onCartRefresh) {
      await cartDrawerSlide._onCartRefresh();
      cartDrawerSlide.show();
    }

    if (btn) btn.style.display = "none";
  } catch (error) {
    console.error("Failed to apply selling plan:", error);
  } finally {
    if (btn) {
      btn.classList.remove("loading_tr");
      btn.disabled = false;
    }
  }
}

document.addEventListener("click", function (e) {
  const btn = e.target.closest(".subscribe-btn");
  if (!btn) return;

  e.preventDefault();

  applySellingPlan({
    lineKey: btn.dataset.lineKey,
    sellingPlanId: btn.dataset.sellingPlanId,
    quantity: btn.dataset.quantity,
    btn
  });
});








let pristineFetch = null;

function cleanFetch(url, options) {
  if (pristineFetch === null) {
    try {
      const frame = document.createElement("iframe");
      frame.setAttribute("aria-hidden", "true");
      frame.setAttribute("tabindex", "-1");
      frame.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";
      document.documentElement.appendChild(frame);
      pristineFetch = frame.contentWindow.fetch.bind(frame.contentWindow);
    } catch (error) {
      pristineFetch = false;
    }
  }

  return (pristineFetch || window.fetch)(url, options);
}

async function upsellAddToCart(button) {
  const variantId = button.dataset.variantId;

  if (!variantId || button.getAttribute("aria-busy") === "true") return;

  const root = window.Shopify?.routes?.root || "/";

  const sections = ["variant-added"];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
  );

  button.setAttribute("aria-busy", "true");

  try {
    const response = await cleanFetch(`${root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest" // Needed for Shopify to check inventory
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(button.dataset.quantity) || 1 }],
        sections: sections.join(","),
        sections_url: `${root}variants/${variantId}`
      })
    });

    const data = await response.json();

    if (!response.ok) {
      button.dispatchEvent(
        new CustomEvent("cart:error", { bubbles: true, detail: { error: data?.description } })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return;
    }

    if (
      window.themeVariables?.settings?.cartType === "page" ||
      window.themeVariables?.settings?.pageType === "cart"
    ) {
      window.location.href = `${root}cart`;
      return;
    }

    const cart = await (await cleanFetch(`${root}cart.js`)).json();
    cart.sections = data.sections;

    button.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: { items: data.items || [data], cart }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "variant:add", cart }
      })
    );
  } catch (error) {
    console.error("Upsell add to cart failed:", error);
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  } finally {
    button.removeAttribute("aria-busy");
  }
}

document.addEventListener("click", function (e) {
  const button = e.target.closest("[data-upsell-add]");
  if (!button) return;

  e.preventDefault();
  upsellAddToCart(button);
});
